import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem("recon_admin") || "");
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [emails, setEmails] = useState(null);
  const [debrief, setDebrief] = useState(null);
  const [testNote, setTestNote] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (tok) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sales`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setData(data);
      const em = await axios.get(`${API}/admin/emails`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setEmails(em.data);
      const deb = await axios.get(`${API}/admin/debrief`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setDebrief(deb.data);
      setError("");
    } catch (e) {
      sessionStorage.removeItem("recon_admin");
      setToken("");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      sessionStorage.setItem("recon_admin", data.token);
      setToken(data.token);
      load(data.token);
    } catch (err) {
      setError("Incorrect passcode.");
    }
  };

  const exportCsv = async () => {
    const res = await axios.get(`${API}/admin/sales.csv`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "recon-2026-buyers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const signOut = () => {
    sessionStorage.removeItem("recon_admin");
    setToken("");
    setData(null);
    setEmails(null);
    setPasscode("");
  };

  const sendTest = async (templateId) => {
    setTestNote("SENDING...");
    try {
      await axios.post(
        `${API}/admin/emails/test`,
        { template_id: templateId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestNote(`TEST SENT — ${templateId.toUpperCase()} → CHECK WEMEETWEEAT1@GMAIL.COM`);
    } catch (e) {
      setTestNote("TEST SEND FAILED — TRY AGAIN");
    }
  };

  return (
    <div className="min-h-screen bg-bone">
      <header className="border-b border-ink/15">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5 md:px-10">
          <a href="/" data-testid="admin-brand-link" className="flex items-center gap-3">
            <img
              src="/assets/wmwe-logo-dark.png"
              alt="WMWE logo"
              className="h-9 w-9 rounded-full border border-ink/15 bg-cream object-contain p-[3px]"
            />
            <span className="font-sans text-[11px] tracking-[0.35em] text-ink">
              RECON 2026 <span className="text-ink/40">// SALES INTELLIGENCE</span>
            </span>
          </a>
          {token && (
            <button
              type="button"
              onClick={signOut}
              data-testid="admin-signout-button"
              className="font-sans text-[10px] tracking-[0.3em] text-ink/50 underline underline-offset-4 transition-colors duration-200 hover:text-wine"
            >
              LOCK FILE →
            </button>
          )}
        </div>
      </header>

      {!token ? (
        <main className="flex items-center justify-center px-6 py-28">
          <form
            onSubmit={login}
            className="w-full max-w-md border border-ink/25 bg-manila p-10 md:p-12"
            data-testid="admin-login-form"
          >
            <p className="font-sans text-[10px] tracking-[0.4em] text-wine">
              RESTRICTED FILE // AUTHORIZED EYES ONLY
            </p>
            <h1 className="mt-5 font-serif text-4xl font-medium tracking-tight text-ink">
              Sales <span className="italic text-wine">Intelligence.</span>
            </h1>
            <label className="mt-8 block font-sans text-[10px] tracking-[0.3em] text-ink/50">
              PASSCODE
            </label>
            <input
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              data-testid="admin-passcode-input"
              className="mt-2 w-full border border-ink/30 bg-bone px-4 py-3 font-sans text-sm tracking-[0.2em] text-ink outline-none transition-colors duration-200 focus:border-wine"
              autoFocus
            />
            {error && (
              <p data-testid="admin-login-error" className="mt-3 font-sans text-[10px] tracking-[0.2em] text-wine">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              className="group relative mt-6 inline-flex w-full items-center justify-center overflow-hidden border border-wine bg-wine px-8 py-4 font-sans text-[10px] tracking-[0.4em] text-bone"
            >
              <span className="absolute inset-0 translate-y-full bg-navy transition-transform duration-500 ease-out group-hover:translate-y-0" />
              <span className="relative">UNLOCK FILE</span>
            </button>
          </form>
        </main>
      ) : (
        <main className="mx-auto max-w-6xl px-6 py-14 md:px-10" data-testid="admin-dashboard">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-sans text-[11px] uppercase tracking-[0.4em] text-wine">
                The Room So Far
              </p>
              <h1 className="mt-4 font-serif text-4xl font-medium tracking-tight text-ink md:text-5xl">
                Seats <span className="italic text-wine">gathered.</span>
              </h1>
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => load(token)}
                data-testid="admin-refresh-button"
                className="border border-ink/30 px-6 py-3 font-sans text-[10px] tracking-[0.3em] text-ink transition-colors duration-200 hover:border-wine hover:text-wine"
              >
                {loading ? "LOADING..." : "REFRESH"}
              </button>
              <button
                type="button"
                onClick={exportCsv}
                data-testid="admin-export-button"
                className="border border-wine bg-wine px-6 py-3 font-sans text-[10px] tracking-[0.3em] text-bone transition-colors duration-200 hover:bg-navy"
              >
                EXPORT BUYER LIST (CSV)
              </button>
            </div>
          </div>

          {data && (
            <>
              <div className="mt-12 grid gap-px border border-ink/20 bg-ink/20 md:grid-cols-4">
                {Object.entries(data.tiers).map(([id, t]) => (
                  <div key={id} data-testid={`admin-tier-${id}`} className="bg-bone p-7">
                    <p className="font-sans text-[9px] tracking-[0.25em] text-ink/45">
                      {t.name.toUpperCase()}
                    </p>
                    <p className="mt-4 font-serif text-4xl font-medium text-ink">
                      {t.sold}
                      <span className="text-xl text-ink/40"> / {t.capacity}</span>
                    </p>
                    <p className="mt-2 font-sans text-[9px] tracking-[0.25em] text-wine">
                      {t.remaining} REMAINING
                    </p>
                    <p className="mt-1 font-sans text-[9px] tracking-[0.25em] text-ink/50">
                      ${t.revenue.toLocaleString()} COLLECTED
                    </p>
                  </div>
                ))}
                <div data-testid="admin-total-revenue" className="bg-wine p-7 text-bone">
                  <p className="font-sans text-[9px] tracking-[0.25em] text-bone/60">
                    TOTAL COLLECTED
                  </p>
                  <p className="mt-4 font-serif text-4xl font-medium">
                    ${data.total_revenue.toLocaleString()}
                  </p>
                  <p className="mt-2 font-sans text-[9px] tracking-[0.25em] text-gold">
                    {data.orders.length} PAID ORDER{data.orders.length === 1 ? "" : "S"}
                  </p>
                </div>
              </div>

              <div className="mt-12 border border-ink/20">
                <p className="border-b border-ink/20 bg-manila px-6 py-4 font-sans text-[10px] tracking-[0.35em] text-ink/60">
                  BUYER FILE // PAID ORDERS
                </p>
                {data.orders.length === 0 ? (
                  <p data-testid="admin-no-orders" className="px-6 py-10 text-sm text-smoke">
                    No paid orders yet — the first seat sold will appear here with the buyer's
                    email.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left" data-testid="admin-orders-table">
                      <thead>
                        <tr className="border-b border-ink/15 font-sans text-[9px] tracking-[0.25em] text-ink/45">
                          <th className="px-6 py-3 font-normal">NAME</th>
                          <th className="px-6 py-3 font-normal">EMAIL</th>
                          <th className="px-6 py-3 font-normal">PHONE</th>
                          <th className="px-6 py-3 font-normal">TICKET</th>
                          <th className="px-6 py-3 font-normal">SEATS</th>
                          <th className="px-6 py-3 font-normal">AMOUNT</th>
                          <th className="px-6 py-3 font-normal">DATE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.orders.map((o) => (
                          <tr key={o.session_id} className="border-b border-ink/10 text-sm">
                            <td className="px-6 py-3 text-smoke">
                              {[o.customer_first_name, o.customer_last_name].filter(Boolean).join(" ") || "—"}
                            </td>
                            <td className="px-6 py-3 font-sans text-xs text-ink">
                              {o.customer_email || "—"}
                            </td>
                            <td className="px-6 py-3 font-sans text-xs text-smoke">
                              {o.customer_phone || "—"}
                            </td>
                            <td className="px-6 py-3 text-smoke">{o.package_name}</td>
                            <td className="px-6 py-3 text-smoke">{o.quantity}</td>
                            <td className="px-6 py-3 text-smoke">
                              ${o.amount?.toLocaleString()}
                            </td>
                            <td className="px-6 py-3 font-sans text-xs text-smoke">
                              {String(o.created_at || "").slice(0, 10)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="mt-12 border border-ink/20" data-testid="admin-email-sequence">
                <p className="border-b border-ink/20 bg-manila px-6 py-4 font-sans text-[10px] tracking-[0.35em] text-ink/60">
                  EMAIL SEQUENCE // COUNTDOWN TO EVENT DAY
                </p>
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink/10 px-6 py-4">
                  <div>
                    <p className="font-serif text-lg text-ink">Purchase Confirmation</p>
                    <p className="font-sans text-[10px] tracking-[0.2em] text-ink/50">
                      AUTOMATIC ON EVERY PAID ORDER // {emails?.confirmations_sent ?? 0} SENT
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => sendTest("confirm-vip")}
                    data-testid="email-test-confirm"
                    className="border border-ink/30 px-4 py-2 font-sans text-[9px] tracking-[0.3em] text-ink/60 transition-colors duration-200 hover:border-wine hover:text-wine"
                  >
                    SEND TEST
                  </button>
                </div>
                {(emails?.campaigns || []).map((c) => (
                  <div
                    key={c.id}
                    data-testid={`email-campaign-${c.id}`}
                    className="flex flex-wrap items-center justify-between gap-3 border-b border-ink/10 px-6 py-4"
                  >
                    <div>
                      <p className="font-serif text-lg text-ink">{c.name}</p>
                      <p className="font-sans text-[10px] tracking-[0.2em] text-ink/50">
                        {new Date(c.send_at).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}{" "}
                        // {c.audience === "evening" ? "LAST SUPPER + FULL BRIEFING" : "ALL BUYERS"}{" "}
                        // {c.sent} SENT
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => sendTest(c.id)}
                      data-testid={`email-test-${c.id}`}
                      className="border border-ink/30 px-4 py-2 font-sans text-[9px] tracking-[0.3em] text-ink/60 transition-colors duration-200 hover:border-wine hover:text-wine"
                    >
                      SEND TEST
                    </button>
                  </div>
                ))}
                {testNote && (
                  <p data-testid="email-test-note" className="px-6 py-4 font-sans text-[10px] tracking-[0.2em] text-wine">
                    {testNote}
                  </p>
                )}
              </div>

              <div className="mt-12 border border-ink/20" data-testid="admin-debrief">
                <p className="border-b border-ink/20 bg-manila px-6 py-4 font-sans text-[10px] tracking-[0.35em] text-ink/60">
                  THE DEBRIEF // POST-EVENT INTELLIGENCE
                </p>
                <div className="flex flex-wrap gap-10 border-b border-ink/10 px-6 py-5">
                  <p className="font-sans text-[10px] tracking-[0.25em] text-ink/60">
                    RESPONSES: <span className="font-serif text-2xl text-ink">{debrief?.count ?? 0}</span>
                  </p>
                  <p className="font-sans text-[10px] tracking-[0.25em] text-ink/60">
                    AVG RATING: <span className="font-serif text-2xl text-ink">{debrief?.average_rating ?? "—"}/5</span>
                  </p>
                  <p className="font-sans text-[10px] tracking-[0.25em] text-ink/60">
                    SHAREABLE TESTIMONIES: <span className="font-serif text-2xl text-ink">{debrief?.testimonials?.length ?? 0}</span>
                  </p>
                </div>
                {(debrief?.testimonials || []).map((t, i) => (
                  <div key={i} data-testid={`debrief-testimonial-${i}`} className="border-b border-ink/10 px-6 py-4">
                    <p className="font-serif text-lg italic text-ink">"{t.testimonial}"</p>
                    <p className="mt-1 font-sans text-[9px] tracking-[0.25em] text-ink/45">
                      {(t.name || "ANONYMOUS").toUpperCase()} // RATING {t.rating}/5
                    </p>
                  </div>
                ))}
                {debrief && debrief.count === 0 && (
                  <p className="px-6 py-8 text-sm text-smoke">
                    No debrief responses yet — they arrive after the Nov 15 email goes out. The
                    survey lives at recon.wemeetweeat.co/debrief.
                  </p>
                )}
              </div>
            </>
          )}
        </main>
      )}
    </div>
  );
}
